'use client'
import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react'
import { useMutation } from '@apollo/client'
import { CUSTOMER_ADD_TO_CART, CUSTOMER_UPDATE_CART_ITEM, CUSTOMER_DELETE_FROM_CART, CUSTOMER_BULK_ADD_TO_CART, CART_DELIVERY_DATE } from '@/api/graphql/cart'
import { E_CART_LINE_TYPE } from '@/schema'
import type { TypeCmsCart, TypeCmsCartLine, TypeCmsVariant } from '@/schema'
import { useSession, useToast, useCartNotifications } from '@/hooks'
import { catchError, DY_COOKIE_DOMAIN, DY_COOKIE_GUEST_CART, DY_GUEST_CART_ALLOWED } from '@/utils'
import { getCartAction, clearCartAction } from '@/app/actions/cartActions'
import { serverCookieRemoveByMaxAge } from '@/app/actions/cookieActions'

// Action types for cart updates
type CartAction =
  | { type: 'ADD'; variant: TypeCmsVariant; quantity: number }
  | { type: 'UPDATE'; variantId: number; quantity: number }
  | { type: 'REMOVE'; variantId: number }
  | { type: 'ADD_MULTIPLE'; items: Array<{ variant: TypeCmsVariant; quantity: number }> }

type CartContextType = {
  cartLines: TypeCmsCartLine[]
  serverCart: TypeCmsCart | null
  addToCart: (variant: TypeCmsVariant, quantity: number) => void
  updateCartQuantity: (variant: TypeCmsVariant, quantity: number) => void
  removeFromCart: (variant: TypeCmsVariant) => void
  reduceFromCart: (variant: TypeCmsVariant, minSaleUnits: number) => void
  addMultipleToCart: (items: Array<{ variant: TypeCmsVariant; quantity: number }>) => Promise<{ ok: boolean, notifications?: unknown[] }>
  hydrateFromServerCart: ({ cart, cartLines }: { cart?: TypeCmsCart | null, cartLines?: TypeCmsCartLine[] }) => void
  refreshCartAfterLogin: () => Promise<{ ok: boolean }>
  setDeliveryDate: ({ deliveryDate }: { deliveryDate: string }) => Promise<{ ok: boolean, cart?: TypeCmsCart }>
  clearCart: () => Promise<{ ok: boolean }>
  isLoading: boolean
}

const CartContext = createContext<CartContextType | undefined>(undefined)

function getGuestCartCookie(): string | null {
	if (typeof document === 'undefined') return null
	const match = document.cookie.match(new RegExp(`(?:^|; )${DY_COOKIE_GUEST_CART}=([^;]*)`))
	return match ? decodeURIComponent(match[1]) : null
}

function setGuestCartCookie(value: string) {
	if (typeof document === 'undefined') return
	const secure = process.env.NODE_ENV === 'production' ? '; Secure' : ''
	document.cookie = `${DY_COOKIE_GUEST_CART}=${encodeURIComponent(value)}; Domain=${DY_COOKIE_DOMAIN}; Path=/; SameSite=Lax${secure}`
}

// Cart reducer - applies changes to cart state
function cartReducer(state: TypeCmsCartLine[], action: CartAction): TypeCmsCartLine[] {
  console.log('Cart Reducer Action:', action.type)
  switch (action.type) {
    case 'ADD': {
      const { variant, quantity } = action
      const existingIndex = state.findIndex(line => line.variant.id === variant.id)

      if (existingIndex >= 0) {
        // Update existing line
        return state.map((line, i) =>
          i === existingIndex
            ? {
              ...line,
              quantity,
              extendedListPrice: (line.originalPrice || 0) * quantity,
              extendedOriginalPrice: (line.originalPrice || 0) * quantity,
              extendedSalePrice: (line.salePrice || 0) * quantity,
            }
            : line
        )
      }

      // New line
      const newCartLine: TypeCmsCartLine = {
        id: Date.now(),
        bigcommerceId: variant.bigcommerceId,
        extendedListPrice: (variant.price?.retailPrice || 0) * quantity,
        originalPrice: variant.price?.retailPrice || variant.price?.salePrice || 0,
        cartLineType: E_CART_LINE_TYPE.PHYSICAL,
        extendedOriginalPrice: (variant.price?.retailPrice || 0) * quantity,
        quantity,
        salePrice: variant.price?.salePrice || 0,
        extendedSalePrice: (variant.price?.salePrice || 0) * quantity,
        product: variant.product!,
        variant,
      }
      return [...state, newCartLine]
    }

    case 'UPDATE': {
      const { variantId, quantity } = action
      if (quantity <= 0) return state.filter(line => line.variant.id !== variantId)

      return state.map(line => {
        if (line.variant.id === variantId) {
          return {
            ...line,
            quantity,
            extendedListPrice: (line.originalPrice || 0) * quantity,
            extendedOriginalPrice: (line.originalPrice || 0) * quantity,
            extendedSalePrice: (line.salePrice || 0) * quantity,
          }
        }
        return line
      })
    }

    case 'REMOVE': {
      return state.filter(line => line.variant.id !== action.variantId)
    }

    case 'ADD_MULTIPLE': {
      const newState = [...state]
      action.items.forEach(({ variant, quantity }) => {
        const existingIndex = newState.findIndex(line => line.variant.id === variant.id)
        const newCartLine: TypeCmsCartLine = {
          id: Date.now() + variant.id,
          bigcommerceId: variant.bigcommerceId,
          extendedListPrice: (variant.price?.retailPrice || 0) * quantity,
          originalPrice: variant.price?.retailPrice || variant.price?.salePrice || 0,
          cartLineType: E_CART_LINE_TYPE.PHYSICAL,
          extendedOriginalPrice: (variant.price?.retailPrice || 0) * quantity,
          quantity,
          salePrice: variant.price?.salePrice || 0,
          extendedSalePrice: (variant.price?.salePrice || 0) * quantity,
          product: variant.product!,
          variant,
        }

        if (existingIndex >= 0) {
          newState[existingIndex] = newCartLine
        } else {
          newState.push(newCartLine)
        }
      })
      return newState
    }

    default:
      return state
  }
}

export function CartProvider({ children, initialCart = [] }: { children: ReactNode; initialCart?: TypeCmsCartLine[] }) {
  // Server state (source of truth from API)
  const [serverCart, setServerCartState] = useState<TypeCmsCart | null>(null)

  // Local state (optimistic UI - what user sees)
  const [localCartLines, setLocalCartLines] = useState<TypeCmsCartLine[]>(initialCart)

  const [isLoading, setIsLoading] = useState(false)

  const { customerToken, isAuthenticated } = useSession()
  const { addToast } = useToast()
  const { handleNotifications } = useCartNotifications()

  // GraphQL mutations
  const [cartLinesAdd] = useMutation(CUSTOMER_ADD_TO_CART, { fetchPolicy: 'no-cache' })
  const [cartLinesBulkAdd] = useMutation(CUSTOMER_BULK_ADD_TO_CART, { fetchPolicy: 'no-cache' })
  const [cartLinesUpdate] = useMutation(CUSTOMER_UPDATE_CART_ITEM, { fetchPolicy: 'no-cache' })
  const [cartLinesDelete] = useMutation(CUSTOMER_DELETE_FROM_CART, { fetchPolicy: 'no-cache' })
  const [cartDeliveryDate] = useMutation(CART_DELIVERY_DATE, { fetchPolicy: 'no-cache' })

  // Debouncing refs
  const pendingChangesRef = useRef<Map<number, number>>(new Map())
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const initialCartRef = useRef(initialCart)
  const localCartLinesRef = useRef(localCartLines)
  const serverCartRef = useRef<TypeCmsCart | null>(null)

  const hydrateFromServerCart = useCallback(({ cart, cartLines }: { cart?: TypeCmsCart | null, cartLines?: TypeCmsCartLine[] }) => {
    if (cart) {
      setServerCartState(cart)
      setLocalCartLines(cart.cartLines)
      return
    }
    if (Array.isArray(cartLines)) {
      setLocalCartLines(cartLines)
    }
  }, [])

  const applyServerCart = useCallback((cart: TypeCmsCart | null | undefined) => {
    if (!cart) return
    setServerCartState(cart)
    setLocalCartLines(cart.cartLines)
  }, [])

  useEffect(() => { localCartLinesRef.current = localCartLines }, [localCartLines])
  useEffect(() => { serverCartRef.current = serverCart }, [serverCart])
  useEffect(() => { initialCartRef.current = initialCart }, [initialCart])

  // Apply optimistic update to local cart (instant UI feedback)
  const applyOptimisticUpdate = useCallback((action: CartAction) => {
    setLocalCartLines(prevCart => cartReducer(prevCart, action))
  }, [])


  // Process debounced mutations (use local currentCart so each iteration sees previous mutation result)
  const processPendingMutations = useCallback(async () => {
    if (pendingChangesRef.current.size === 0 || (!customerToken && !DY_GUEST_CART_ALLOWED)) return

    const changes = new Map(pendingChangesRef.current)
    pendingChangesRef.current.clear()

    const initialServerCart = serverCartRef.current
    const initialCartLines = serverCartRef.current?.cartLines ?? initialCartRef.current
    let currentCart = initialCartLines

    setIsLoading(true)

		const DYGuestCartToken = getGuestCartCookie()
		const context = {
			privateStore: true,
			DYCustomerToken: customerToken,
			DYGuestCartToken
		}

    try {
      for (const [variantId, quantity] of changes) {
        if (quantity === -Infinity || quantity <= 0) {
          const res = await cartLinesDelete({
            variables: { variantId },
            context
          })
          if (res.data?.cartLinesDelete?.cart) {
						const data = res.data.cartLinesDelete
            const cart = data.cart
						const guestToken = data.guestToken
						if (guestToken && guestToken !== getGuestCartCookie()) setGuestCartCookie(guestToken)
            currentCart = cart.cartLines
            applyServerCart(cart)
            handleNotifications(cart)
          }
        } else {
          const existingLine = currentCart.find(line => line.variant.id === variantId)

          if (existingLine) {
            const res = await cartLinesUpdate({
              variables: { variantId, quantity, cartLineType: E_CART_LINE_TYPE.PHYSICAL },
              context
            })
            if (res.data?.cartLinesUpdate?.cart) {
							const data = res.data.cartLinesUpdate
              const cart = data.cart
							const guestToken = data.guestToken
							if (guestToken && guestToken !== getGuestCartCookie()) setGuestCartCookie(guestToken)
              currentCart = cart.cartLines
              applyServerCart(cart)
              handleNotifications(cart)
            }
          } else {
            const res = await cartLinesAdd({
              variables: { variantId, quantity, cartLineType: E_CART_LINE_TYPE.PHYSICAL },
              context
            })

            if (res.data?.cartLinesAdd?.cart) {
							const data = res.data.cartLinesAdd
              const cart = data.cart
							const guestToken = data.guestToken
							if (guestToken && guestToken !== getGuestCartCookie()) setGuestCartCookie(guestToken)
              currentCart = cart.cartLines
              applyServerCart(cart)
              handleNotifications(cart)
            }
          }
        }
      }
    } catch (error) {
      console.error('Cart mutation failed:', error)
      setLocalCartLines(initialCartLines)
      setServerCartState(initialServerCart)
      addToast({
        message: 'Failed to update cart. Please try again.',
        type: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [customerToken, cartLinesDelete, applyServerCart, handleNotifications, cartLinesUpdate, cartLinesAdd, addToast])

  // Schedule debounced mutation
  const scheduleMutation = useCallback((variantId: number, quantity: number) => {
    pendingChangesRef.current.set(variantId, quantity)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
			console.log('processPendingMutations')
      processPendingMutations()
    }, 300)
  }, [processPendingMutations])

  // Public API
  const addToCart = useCallback((variant: TypeCmsVariant, quantity: number) => {
    const currentLine = localCartLinesRef.current.find(line => line.variant.id === variant.id)
    const finalQuantity = (currentLine?.quantity || 0) + quantity
    applyOptimisticUpdate({ type: 'ADD', variant, quantity: finalQuantity })
    scheduleMutation(variant.id, finalQuantity)
  }, [applyOptimisticUpdate, scheduleMutation])

  const removeFromCart = useCallback((variant: TypeCmsVariant) => {
    // Apply optimistic update
    applyOptimisticUpdate({ type: 'REMOVE', variantId: variant.id })

    // Schedule backend sync
    scheduleMutation(variant.id, -Infinity)
  }, [applyOptimisticUpdate, scheduleMutation])

  const updateCartQuantity = useCallback((variant: TypeCmsVariant, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(variant)
      return
    }

    // Apply optimistic update
    applyOptimisticUpdate({ type: 'UPDATE', variantId: variant.id, quantity })

    // Schedule backend sync
    scheduleMutation(variant.id, quantity)
  }, [applyOptimisticUpdate, scheduleMutation, removeFromCart])

  const reduceFromCart = useCallback((variant: TypeCmsVariant, minSaleUnits: number) => {
    const currentLine = localCartLinesRef.current.find(line => line.variant.id === variant.id)
    if (!currentLine) return

    const newQuantity = currentLine.quantity - minSaleUnits

    if (newQuantity <= 0) {
      removeFromCart(variant)
    } else {
      updateCartQuantity(variant, newQuantity)
    }
  }, [removeFromCart, updateCartQuantity])

  const addMultipleToCart = useCallback(async (items: Array<{ variant: TypeCmsVariant; quantity: number }>) => {
    if (!customerToken) {
      addToast({ message: 'Please log in to add items to cart', type: 'error' })
      return
    }

    // Apply optimistic update
    applyOptimisticUpdate({ type: 'ADD_MULTIPLE', items })

    try {
      // Use bulk mutation instead of multiple individual calls
      const cartLines = items.map(({ variant, quantity }) => ({
        variantId: variant.id,
        quantity,
        cartLineType: E_CART_LINE_TYPE.PHYSICAL,
      }))

      const result = await cartLinesBulkAdd({
        variables: { cartLines },
        context: { privateStore: true, DYCustomerToken: customerToken },
      })

      if (result.data?.cartLinesAdd?.cart) {
        const cart = result.data.cartLinesAdd.cart
        applyServerCart(cart)
        handleNotifications(cart)
        return { ok: true, notifications: cart.notifications || []}
      }
    } catch (error) {
      console.error('Failed to add multiple items:', error)
      const errorRes = catchError(error)
      console.log('errorRes:', errorRes)
      // Rollback on error
      setLocalCartLines(serverCartRef.current?.cartLines ?? initialCartRef.current)
      addToast({ message: 'Some items could not be added to cart', type: 'error' })
    }

    return {
      ok: false
    }
  }, [customerToken, applyOptimisticUpdate, addToast, cartLinesBulkAdd, handleNotifications, applyServerCart])

  const refreshCartAfterLogin = useCallback(async () => {
    const cartResult = await getCartAction()
    if (!cartResult.ok) return { ok: false }
    hydrateFromServerCart({ cart: cartResult.cart, cartLines: cartResult.cartLines })
    return { ok: true }
  }, [hydrateFromServerCart])

  const setDeliveryDate = useCallback(async ({ deliveryDate }: { deliveryDate: string }) => {
    if (!customerToken) return { ok: false }

    try {
      const result = await cartDeliveryDate({
        variables: { deliveryDate },
        context: { privateStore: true, DYCustomerToken: customerToken },
      })

      const cart = result.data?.cartDeliveryDate?.cart
      if (!cart) return { ok: false }

      applyServerCart(cart)
      handleNotifications(cart)
      return { ok: true, cart }
    } catch {
      return { ok: false }
    }
  }, [customerToken, cartDeliveryDate, applyServerCart, handleNotifications])

  const clearCart = useCallback(async () => {
    if (!isAuthenticated) {
      await serverCookieRemoveByMaxAge(DY_COOKIE_GUEST_CART)
      setLocalCartLines([])
      setServerCartState(null)
      return { ok: true }
    }

    const result = await clearCartAction()
    if (!result.ok) return { ok: false }

    const clearedCart = result.data?.cart ?? null
    setServerCartState(clearedCart)
    setLocalCartLines(clearedCart?.cartLines ?? [])
    return { ok: true }
  }, [isAuthenticated])

  const value = useMemo(
    () => ({
      cartLines: localCartLines,
      serverCart,
      addToCart,
      updateCartQuantity,
      removeFromCart,
      reduceFromCart,
      addMultipleToCart,
      hydrateFromServerCart,
      refreshCartAfterLogin,
      setDeliveryDate,
      clearCart,
      isLoading,
    }),
    [
      localCartLines,
      serverCart,
      addToCart,
      updateCartQuantity,
      removeFromCart,
      reduceFromCart,
      addMultipleToCart,
      hydrateFromServerCart,
      refreshCartAfterLogin,
      setDeliveryDate,
      clearCart,
      isLoading,
    ]
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within CartProvider')
  }
  return context
}
